import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Upload, Zap, Shield, Clock, ArrowRight, ChevronDown, ChevronUp, SlidersHorizontal, Thermometer, Layers, BadgeCheck } from 'lucide-react';
import TrustBenefitsSection from '../components/TrustBenefitsSection';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.45, ease: 'easeOut' } })
};

const materials = [
  { name: 'PLA', desc: 'Budget friendly, high strength', specs: ['Tensile: 47-65 MPa', 'HDT: 52°C'] },
  { name: 'High Temp PLA', desc: 'Heat-resistant, annealable', specs: ['Tensile: 55-70 MPa', 'HDT: 140°C'] },
  { name: 'PETG', desc: 'Durable, chemical resistant', specs: ['Tensile: 45-55 MPa', 'HDT: 70°C'] },
  { name: 'ABS', desc: 'Impact resistant, tough', specs: ['Tensile: 40-50 MPa', 'HDT: 98°C'] },
  { name: 'TPU', desc: 'Flexible elastomer', specs: ['Shore: 95A', 'Elongation: 580%'] },
  { name: 'PPS-CF', desc: 'High-temp carbon fiber', specs: ['Tensile: 87-105 MPa', 'HDT: 252°C'] },
];

const faqs = [
  { q: 'What file formats do you accept?', a: 'We accept STL, OBJ, 3MF, STEP, STP, PLY, and AMF files. STL is the most common format for 3D printing.' },
  { q: 'How fast can I get my parts?', a: 'Standard turnaround is 3-5 business days. Rush orders (1-2 days) are available for an additional fee.' },
  { q: 'What materials are available?', a: 'We offer PLA, PLA+, PETG, ABS, ASA, TPU, Nylon, PC, PPS-CF, and specialty filaments including carbon fiber reinforced, wood-fill, and metal-fill materials.' },
  { q: 'How is pricing calculated?', a: 'Pricing is based on material usage, print time, post-processing requirements, and quantity. Upload your file to get an instant, accurate quote.' },
  { q: 'Are my files kept confidential?', a: 'Absolutely. All uploaded files are encrypted and stored securely. We never share your designs with third parties.' },
];

const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group">
        <span className="font-medium text-slate-900 group-hover:text-[#0007b5] transition-colors pr-4">{q}</span>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
      </button>
      {open && <p className="pb-5 text-slate-600 text-sm leading-relaxed">{a}</p>}
    </div>
  );
};

const InstantQuoteLanding = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const goToTool = () => navigate('/get-started?service=on-demand-3d-print');
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      navigate('/get-started?service=on-demand-3d-print', { state: { uploadedFile: file } });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      navigate('/get-started?service=on-demand-3d-print', { state: { uploadedFile: file } });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center" data-testid="landing-logo">
            <img src="/logo-nav.png" alt="Create 3D Works" className="h-8 object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/faq" className="text-sm text-slate-600 hover:text-[#0007b5] transition-colors font-medium">FAQ</a>
            <a href="/contact" className="text-sm text-slate-600 hover:text-[#0007b5] transition-colors font-medium">Contact</a>
          </nav>
          <button
            data-testid="header-cta"
            onClick={goToTool}
            className="px-6 py-2.5 bg-[#0007b5] text-white text-base font-semibold rounded-lg hover:bg-[#00058F] transition-colors flex items-center gap-2 shadow-md shadow-blue-500/20"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_801805a1-56d6-4a1b-9dc4-a3c271402139/artifacts/2uma28ub_Asset%205%401x.png" 
              alt="" 
              className="w-5 h-5 object-contain" 
            />
            Get a Quote
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-white pt-16 pb-20 lg:pt-20 lg:pb-24 relative">
        <div className="max-w-3xl lg:max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="text-[#0007b5] text-sm font-semibold uppercase tracking-widest mb-5">Instant 3D Print Pricing</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-5">
              Get an Instant Quote<br /><span className="text-[#0007b5] lg:whitespace-nowrap">Order your parts in seconds</span>
            </h1>
            <p className="text-lg text-slate-500 max-w-xl mx-auto mb-10 leading-relaxed">
              Upload your 3D model, configure materials and print settings, and receive an accurate quote in seconds. No waiting, no back-and-forth.
            </p>

            {/* Illustration above dropzone */}
            <img
              src="https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/cpje1y0a_C3D%20Works%20Service%201%20V2%20%28R%29.png"
              alt="Upload, configure, and print — three-step instant quote flow"
              className="w-full h-auto object-contain max-w-3xl mx-auto mb-8 scale-[1.2]"
              loading="eager"
            />

            {/* Dropzone — aligned to text width, more square */}
            <div
              data-testid="hero-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`mx-auto max-w-xl aspect-[8/3] flex flex-col items-center justify-center border-2 border-dashed rounded-2xl px-8 text-center cursor-pointer transition-all duration-300 ${
                dragOver ? 'border-[#0007b5] bg-blue-50/60' : 'border-slate-300 bg-slate-50 hover:border-[#0007b5] hover:bg-blue-50/30'
              }`}
            >
              <div className="w-16 h-16 mb-5 rounded-xl bg-[#0007b5]/10 flex items-center justify-center">
                <Upload className="w-8 h-8 text-[#0007b5]" />
              </div>
              <p className="font-semibold text-slate-900 text-lg mb-2">Drop your 3D file here or click to browse</p>
              <p className="text-sm text-slate-500">STL, STEP, OBJ, 3MF, PLY supported</p>
              <input ref={fileInputRef} type="file" accept=".stl,.step,.stp,.obj,.3mf,.ply,.amf" onChange={handleFileSelect} className="hidden" />
            </div>

            <div className="flex items-center gap-6 mt-5 text-xs text-slate-400 justify-center">
              <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Files encrypted</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Quote in seconds</span>
              <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> No signup required</span>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#0007b5] via-blue-400 to-[#0007b5]" />
      </section>

      {/* Trust & Benefits — editorial, typography-led */}
      <TrustBenefitsSection
        testId="instant-quote-trust"
        eyebrow="Real people, real parts"
        marker="check"
        headline={<>You're talking to<br />the people printing<br />your parts.</>}
        lead={[
          "We're a small, hands-on team, not a faceless print farm. When you upload a file, the same engineers and techs who tune our printers are the ones reviewing your geometry and configuration, slicing your file, and pulling your parts off the build plate.",
          "That's how we keep quality high, turnaround fast, and minimize surprises.",
        ]}
        benefits={[
          {
            title: 'A small team you can actually reach',
            desc: "No ticket queues or offshore call centers. Reach out and you'll hear back from a person sitting a few feet away from the printers, usually within hours.",
          },
          {
            title: 'Direct line to the printer behind your parts',
            desc: "Couldn't get the perfect orientation in our CAD editor? Have a question about functional parts, infill settings, surface finish, or something else? Talk to the people who'll actually run the job. Your input shapes the print, not just the order form.",
          },
          {
            title: 'Locally printed, fast turnaround',
            desc: "Every part is printed in-house by us, no overseas shipping, no customs delays. Most orders ship in 3–5 business days, with rush options when you need them sooner.",
          },
          {
            title: 'Secure checkout powered by Stripe',
            desc: "Card details never touch our servers. Stripe handles the payment, with full PCI compliance, fraud protection, and 3D Secure built in. Pay confidently, the same way you would on any major retailer.",
          },
          {
            title: 'Your satisfaction comes first',
            desc: "Parts don\u2019t meet spec? Print quality off? We make it right: reprint, revise, or full refund. Your satisfaction and trust matter more to us than the cost of one job.",
          },
        ]}
      />

      {/* Trust Marquee */}
      <section className="py-8 border-y border-slate-100 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-xs font-semibold text-slate-400 uppercase tracking-widest mb-6">Suited to your application</p>
          <Marquee speed={40} gradient gradientColor="#f8fafc" gradientWidth={80} pauseOnHover>
            {['Prototyping', 'Product Design', 'Automotive', 'Consumer Electronics', 'Robotics', 'Architecture', 'Storage', 'Toys', 'Decorative', 'Cosplay', 'Flight Simulators', 'Organization', 'Sim Racing'].map((item) => (
              <span key={item} className="mx-12 text-slate-400 font-semibold text-lg">{item}</span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* How It Works — white */}
      <section className="py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">How it works</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Upload', desc: 'Drag and drop your 3D model. Preview it instantly in our built-in viewer.' },
              { step: '2', title: 'Configure', desc: 'Choose material, quality, color, and quantity with a live 3D preview.' },
              { step: '3', title: 'Get Quote', desc: 'Receive instant pricing. Add to cart or request changes.' },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="relative bg-white border border-slate-200 rounded-2xl p-8 pt-20 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={i}
              >
                <div className="text-7xl font-bold text-slate-100 absolute top-4 right-6">{item.step}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats band — three-column centered */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-10">
            <motion.div
              className="flex items-center justify-center gap-5"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={0}
            >
              <Zap className="w-10 h-10 text-[#0007b5] flex-shrink-0" strokeWidth={1.75} />
              <div>
                <p className="text-xl font-bold text-slate-900">Parts in as little as 48h</p>
                <p className="text-sm text-slate-500 mt-1">Fast turnaround with rush options available</p>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center justify-center gap-5"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={1}
            >
              <Shield className="w-10 h-10 text-[#0007b5] flex-shrink-0" strokeWidth={1.75} />
              <div>
                <p className="text-xl font-bold text-slate-900">You maintain full IP</p>
                <p className="text-sm text-slate-500 mt-1">Your files are never shared with third parties. Your IP is always yours.</p>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center justify-center gap-5"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={2}
            >
              <BadgeCheck className="w-10 h-10 text-[#0007b5] flex-shrink-0" strokeWidth={1.75} />
              <div>
                <p className="text-xl font-bold text-slate-900">Money-back guarantee</p>
                <p className="text-sm text-slate-500 mt-1">Parts don't meet specs? We'll make it right or refund you</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Materials — white */}
      <section className="py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-3">Engineering-grade materials</h2>
            <p className="text-slate-500 max-w-xl mx-auto">Select from our curated range of FDM materials, each tested for consistent print quality.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {materials.map((mat, i) => (
              <motion.div
                key={mat.name}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0007b5] hover:-translate-y-1 hover:shadow-md transition-all duration-300 group"
                initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp} custom={i}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-slate-900">{mat.name}</h3>
                  <Thermometer className="w-4 h-4 text-slate-300 group-hover:text-[#0007b5] transition-colors" />
                </div>
                <p className="text-sm text-slate-500 mb-3">{mat.desc}</p>
                <div className="space-y-1">
                  {mat.specs.map((spec) => <p key={spec} className="text-xs text-slate-400 font-mono">{spec}</p>)}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="/material-guide" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0007b5] hover:underline">
              View full material guide <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ — light grey */}
      <section className="py-20 lg:py-24 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.h2
            className="text-3xl sm:text-4xl font-bold text-slate-900 text-center tracking-tight mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
          >
            Frequently asked questions
          </motion.h2>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={fadeUp}>
            {faqs.map((faq) => <FAQ key={faq.q} q={faq.q} a={faq.a} />)}
          </motion.div>
        </div>
      </section>

      {/* About Us — short intro */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <motion.div
              className="lg:col-span-7 rounded-2xl overflow-hidden"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_modelcraft-38/artifacts/pmf7iszc_About%20Us%20-%20Parts%20Showcase%202.jpg"
                alt="Showcase of 3D-printed parts produced by Create 3D Works"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              className="lg:col-span-5"
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
            >
              <p className="text-[#0007b5] text-sm font-semibold uppercase tracking-widest mb-4">Our Story</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-5">
                Engineers turning ideas into real parts
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Create 3D Works is built on hands-on expertise and a passion for turning ideas into reality. Our team of engineers and technicians honed their skills as a "skunkworks" 3D printing team, solving real-world problems through advanced additive manufacturing.
              </p>
              <p className="text-slate-600 leading-relaxed">
                With 8+ years of specialized 3D printing experience and 20+ years in engineering and design, we've produced parts for everything from cosmetic displays to functional end-use components; 3,000+ lb. of material and counting.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA — brand blue */}
      <section className="py-20 lg:py-24 bg-[#0007b5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">Ready to get your parts made?</h2>
            <p className="text-lg text-white/70 mb-8">Upload your 3D model and get an instant quote. No account needed.</p>
            <button
              data-testid="cta-bottom"
              onClick={goToTool}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0007b5] rounded-xl font-bold text-lg hover:bg-slate-100 transition-colors shadow-lg"
            >
              Get Your Instant Quote <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-sm text-slate-500">&copy; {new Date().getFullYear()} C3D Works. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="/privacy-policy" className="text-sm text-slate-500 hover:text-[#0007b5] transition-colors">Privacy</a>
              <a href="/terms-of-service" className="text-sm text-slate-500 hover:text-[#0007b5] transition-colors">Terms</a>
              <a href="/contact" className="text-sm text-slate-500 hover:text-[#0007b5] transition-colors">Contact</a>
              <a href="/material-guide" className="text-sm text-slate-500 hover:text-[#0007b5] transition-colors">Materials</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InstantQuoteLanding;
